/**
 * Server-side session revocation for logout.
 *
 * A logout that only clears localStorage leaves the rotating refresh-token
 * family alive on the server: a stolen refresh token kept working after the
 * user "signed out". This module wires the missing half — POSTing the
 * persisted refresh token to the native-auth revocation endpoint
 * (`POST /api/v1/auth/logout`, Marketplace NativeAuthEndpoints), which revokes
 * the presented token's ENTIRE family (single-session logout). The path/shape
 * come from {@link AUTH_GATEWAY_ENDPOINTS} so frontend/gateway drift is caught
 * by the auth contract test.
 *
 * Contract with `AuthProvider.logout` (contexts.tsx):
 *
 *   - The persisted refresh token is captured SYNCHRONOUSLY, before this
 *     function's first `await`, so the caller may clear local auth state
 *     immediately after invoking it — the in-flight POST still carries the
 *     token. Local logout is never trapped behind the network call.
 *   - Never rejects. A failed revocation is surfaced honestly (not
 *     swallowed): a `console.warn` naming the route plus an
 *     {@link LOGOUT_REVOCATION_FAILED_EVENT} event for shell telemetry, and a
 *     `'failed'` result. The user is still logged out locally either way.
 *
 * Lanes without a persisted native refresh token — dev tokens and the
 * OIDC/Keycloak SSO lane (whose gateway surface exposes no logout endpoint;
 * only `/auth/sso/refresh` is proxied) — have no server-side session this
 * endpoint can revoke, so they resolve `'no-refresh-token'` without a network
 * call.
 */

import { AUTH_GATEWAY_ENDPOINTS } from './authGatewayContract.ts';
import type { LogoutRequestBody } from './authGatewayContract.ts';
import { buildApiUrl } from './apiFetch.ts';
import { readRefreshToken } from './tokenRefresh.ts';

/** Outcome of the server-side revocation attempt — honest, three-state. */
export type LogoutRevocationResult = 'revoked' | 'no-refresh-token' | 'failed';

/**
 * Dispatched on `window` when the revocation POST fails (network error or
 * non-2xx). `detail: { reason }`. Non-blocking: local logout has already
 * proceeded; the server-side family stays alive until expiry or replay
 * detection.
 */
export const LOGOUT_REVOCATION_FAILED_EVENT = 'auth:logout-revocation-failed';

/**
 * Revoke the current session's refresh-token family on the server.
 * Reads the persisted refresh token synchronously (see module contract), then
 * POSTs it to the native logout endpoint. Never rejects.
 */
export const revokeSessionOnServer = (): Promise<LogoutRevocationResult> => {
  // Synchronous capture — MUST happen before the caller wipes localStorage.
  const refreshToken = readRefreshToken();
  if (!refreshToken) return Promise.resolve('no-refresh-token');
  return postLogout(refreshToken);
};

/**
 * The concrete route this module POSTs. Kept as a literal (not composed from
 * the descriptor) so endpoint-reachability tooling sees the caller;
 * logoutRevocation.test.ts pins it equal to
 * `gatewayPath(AUTH_GATEWAY_ENDPOINTS.logout.path)` so it cannot drift.
 */
export const LOGOUT_ENDPOINT = '/api/v1/auth/logout';

async function postLogout(refreshToken: string): Promise<LogoutRevocationResult> {
  const body: LogoutRequestBody = { refreshToken };
  try {
    const res = await fetch(buildApiUrl(LOGOUT_ENDPOINT), {
      method: AUTH_GATEWAY_ENDPOINTS.logout.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(10_000),
    });
    // The endpoint is enumeration-safe (always 200 for any token), so any
    // non-2xx means the revocation genuinely did not run.
    if (!res.ok) return reportFailure(`status ${res.status}`);
    return 'revoked';
  } catch (err) {
    return reportFailure(err instanceof Error ? err.message : String(err));
  }
}

function reportFailure(reason: string): LogoutRevocationResult {
  // Honest, non-blocking surfacing: the local logout has already proceeded,
  // but the server-side refresh-token family was NOT revoked.
  console.warn(
    `POST /api/v1/auth/logout failed (${reason}); ` +
      'server-side refresh-token-family revocation did not complete.',
  );
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(LOGOUT_REVOCATION_FAILED_EVENT, { detail: { reason } }),
    );
  }
  return 'failed';
}
